import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { List, Card, Avatar, Modal, Tabs, Input, Breadcrumb, Button } from 'antd';
import { UserOutlined, LikeOutlined, CommentOutlined, ShareAltOutlined, HomeOutlined, SendOutlined, SearchOutlined, SmileOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import InfiniteScroll from 'react-infinite-scroll-component';
import BoringAvatar from 'boring-avatars';
import { Dropdown, Menu, Tooltip } from 'antd';
import { Popover } from 'antd';

//Ecards for rewards.
import accelerateinnovationExtraMile from '../../assets/images/Rewards_Feeds_Ecards/accelerateinnovationExtraMile.png';
import accelerateinnovationStellar from '../../assets/images/Rewards_Feeds_Ecards/accelerateinnovationStellar.png';
import accelerateinnovationCheers from '../../assets/images/Rewards_Feeds_Ecards/accelerateinnovationcheers.png';
import beagileCheers from '../../assets/images/Rewards_Feeds_Ecards/beagileCheers.png';
import beagileExtraMile from '../../assets/images/Rewards_Feeds_Ecards/beagileExtraMile.png';
import beagileStellar from '../../assets/images/Rewards_Feeds_Ecards/beagileStellar.png';
import driveResultsCheers from '../../assets/images/Rewards_Feeds_Ecards/driveResultsCheers.png';
import driveResultsExtraMile from '../../assets/images/Rewards_Feeds_Ecards/driveResultsExtraMile.png';
import driveResultsStellar from '../../assets/images/Rewards_Feeds_Ecards/driveResultsStellar.png';
import growTogetherCheers from '../../assets/images/Rewards_Feeds_Ecards/growTogetherCheers.png';
import growTogetherExtraMile from '../../assets/images/Rewards_Feeds_Ecards/growTogetherExtraMile.png';
import growTogetherStellar from '../../assets/images/Rewards_Feeds_Ecards/growTogetherStellar.png';
// ACE / AVIA images are now served dynamically per year from the server.
// Fallback to bundled images if the server image is not found.
import AceImageFallback from '../../assets/images/Rewards_Feeds_Ecards/ACE.png';
import AviaImageFallback from '../../assets/images/Rewards_Feeds_Ecards/AVIA.png';
import { useTranslation } from 'react-i18next';

const getAwardImageUrl = (baseURL: string | undefined, rewardName: string, feedDate: string): string => {
  const date  = feedDate ? new Date(feedDate) : new Date();
  const year  = date.getFullYear();
  const half  = date.getMonth() < 6 ? 'H1' : 'H2';   // Jan–Jun = H1, Jul–Dec = H2
  const type  = rewardName.toUpperCase().includes('AVIA') ? 'AVIA' : 'ACE';
  return `${baseURL}/awards/images/${year}/${half}/${type}.png`;
};

const { TabPane } = Tabs;
const { TextArea } = Input;

interface Feed {
  feedId: string;
  celebrationId?: string;
  appreciationId?: string;
  nomiationId?: string;
  nominatorId: string;
  nominatorName: string;
  nomineeId: string;
  nomineeName: string;
  companyValueId?: string;
  companyValueName?: string;
  celebrationCategoryId?: string;
  celebrationCategoryName?: string;
  ecardImage?: string;
  message?: string;
  description: string;
  visibility: boolean;
  createdAt: string;
  updatedAt: string;
  feedDate: string; // Primary date field for frontend sorting
  transactionDate?: string; // Transaction date from backend
  liked?: boolean;
  comments?: { item1: string, item2: string }[];
  likeCount?: { item1: string, item2: string, item3: string }[];
  valueName?: string;
  rewardCategoryName?: string,
  rewardName?: string,
  externalDocuments?: string[],
  image?: string,
}

const EMOJI_LIST = [
  '😀','😂','😍','😊','🥰','😎','🤩','😄','😁','😅',
  '🙌','👏','💪','👍','🙏','❤️','🔥','🎉','🎊','🌟',
  '✨','💯','😢','😮','🤔','😆','😜','🥳','😇','😏',
  '🤗','😬','😴','🤣','😋','🥺','😤','😡','🤯','🫶',
];

// Optimized: Memoized FeedItem component to prevent unnecessary re-renders
const FeedItem = React.memo<{
  item: Feed;
  userProfileImages: { [key: string]: string | null };
  emojiReactions: { [key: string]: string | null };
  visibleComments: { [key: string]: boolean };
  showAllComments: { [key: string]: boolean };
  commentValues: { [key: string]: string };
  postingComment: { [key: string]: boolean };
  comments: { [key: string]: { item1: string, item2: string }[] };
  userNames: { [key: string]: string };
  nominatorEmail: string;
  onEmojiReaction: (feedId: string, emojiCode: string) => void;
  onToggleComments: (feedId: string) => void;
  onToggleShowAllComments: (feedId: string) => void;
  onCommentChange: (feedId: string, value: string) => void;
  onComment: (feedId: string) => void;
  onNameClick: (userId: string) => void;
  mentionResults: { [feedId: string]: { id: string; displayName: string }[] };
  onMentionSelect: (feedId: string, userId: string, displayName: string) => void;
  onClearMentions: (feedId: string) => void;
  formatDate: (date: string) => string;
  getImageSrc: (valueName: string | undefined, rewardName: string | undefined) => string | null;
  extractCitation: (description: string) => string;
  emojiOptions: { code: string; emoji: string }[];
}>(({ 
  item, 
  userProfileImages, 
  emojiReactions, 
  visibleComments, 
  showAllComments,
  commentValues,
  postingComment,
  comments,
  userNames,
  nominatorEmail,
  onEmojiReaction,
  onToggleComments,
  onToggleShowAllComments,
  onCommentChange,
  onComment,
  onNameClick,
  formatDate,
  getImageSrc,
  extractCitation,
  emojiOptions,
  mentionResults,
  onMentionSelect,
  onClearMentions,
}) => {
  const imageSrc = getImageSrc(item.valueName, item.rewardName);
  const [smileyOpen, setSmileyOpen] = useState(false);

  const emojiPickerContent = (
    <div style={{ width: '240px', padding: '6px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {EMOJI_LIST.map((emoji) => (
          <span
            key={emoji}
            onClick={() => {
              onCommentChange(item.feedId, (commentValues[item.feedId] || '') + emoji);
              setSmileyOpen(false);
            }}
            style={{ fontSize: '20px', cursor: 'pointer', padding: '3px', borderRadius: '4px', lineHeight: 1 }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f0ff')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            {emoji}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <List.Item key={item.feedId} style={{ padding: '0', marginBottom: '16px', border: 'none' }}>
      <Card
        bordered
        style={{ width: '100%', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', borderRadius: '8px', overflow: 'hidden' }}
        bodyStyle={{ padding: '15px 20px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
          {!item.nomiationId ? (
            <Avatar src={userProfileImages[item.nominatorId] ? `data:image/jpeg;base64,${userProfileImages[item.nominatorId]}` : undefined} size={40} icon={!userProfileImages[item.nominatorId] && <UserOutlined />} />
          ) : (
            <Avatar src={userProfileImages[item.nominatorId] ? `data:image/jpeg;base64,${userProfileImages[item.nomineeId]}` : undefined} size={40} icon={!userProfileImages[item.nomineeId] && <UserOutlined />} />
          )}
          <div style={{ marginLeft: '10px', flex: 1 }}>
            <div style={{ fontSize: '14px' }}>
              {!item.nomiationId ? (
                <>
                  <strong style={{ marginRight: '5px', cursor: 'pointer' }} onClick={() => onNameClick(item.nominatorId)}>
                    {item.nominatorName}
                  </strong>
                  <span style={{ color: '#666' }}>{item.celebrationId ? 'wishes ' : 'appreciates '}</span>
                </>
              ) : (null)}

              {item.celebrationId ? (
                <>
                  <strong style={{ cursor: 'pointer' }} onClick={() => onNameClick(item.nomineeId)}>
                    {item.nomineeName}
                  </strong> for <strong style={{ color: '#1890ff' }}>{item.celebrationCategoryName}</strong>
                </>
              ) : item.appreciationId ? (
                <>
                  <strong style={{ cursor: 'pointer' }} onClick={() => onNameClick(item.nomineeId)}>
                    {item.nomineeName}
                  </strong> for demonstrating <strong style={{ color: '#1890ff' }}>{item.companyValueName}</strong>
                </>
              ) : (
                <>
                  <strong style={{ cursor: 'pointer' }} onClick={() => onNameClick(item.nomineeId)}>
                    {item.nomineeName}
                  </strong>
                  {' '}has received{' '}
                  <strong style={{ color: '#1890ff' }}>{item.rewardName}</strong>
                  {(
                    <>
                      {' '}
                      from{' '}
                      <strong style={{ cursor: 'pointer' }} onClick={() => onNameClick(item.nominatorId)}>
                        {item.nominatorName}
                      </strong>
                    </>
                  )}
                </>
              )}
            </div>
            <div style={{ color: 'gray', fontSize: '12px' }}>
              {formatDate(item.nomiationId ? (item.transactionDate || item.feedDate || item.updatedAt) : (item.feedDate || item.updatedAt))}
            </div>
          </div>
        </div>

        {/* Rest of the feed item content - keeping original structure */}
        {item.nomiationId ? (
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginTop: '15px' }}>
              <div style={{ width: '100%' }}>
                <p style={{ fontSize: '14px', margin: '5px 20px 10px 20px', wordWrap: 'break-word', whiteSpace: 'normal', textAlign: 'justify' }}>
                  {extractCitation(item.description)}
                </p>
              </div>
              {imageSrc ? (
                <img src={imageSrc} alt="eCard" style={{ width: '100%', objectFit: 'contain', borderRadius: '8px', marginBottom: '10px' }} />
              ) : (item.rewardName === "ACE Individual Award" || item.rewardName === "ACE Team Award") ? (
                <div style={{ marginTop: '15px', textAlign: 'center' }}>
                  <img
                    src={getAwardImageUrl(process.env.REACT_APP_API_URL, item.rewardName, item.transactionDate || item.feedDate || item.createdAt)}
                    alt="ACE Award"
                    onError={(e) => { (e.target as HTMLImageElement).src = AceImageFallback; }}
                    style={{ width: '100%', objectFit: 'contain', borderRadius: '8px', marginBottom: '10px' }}
                  />
                </div>
              ) : (item.rewardName === "AVIA Award") ? (
                <div style={{ marginTop: '15px', textAlign: 'center' }}>
                  <img
                    src={getAwardImageUrl(process.env.REACT_APP_API_URL, item.rewardName, item.transactionDate || item.feedDate || item.createdAt)}
                    alt="AVIA Award"
                    onError={(e) => { (e.target as HTMLImageElement).src = AviaImageFallback; }}
                    style={{ width: '100%', objectFit: 'contain', borderRadius: '8px', marginBottom: '10px' }}
                  />
                </div>
              ) : item.ecardImage ? (
                <div style={{ marginTop: '15px', textAlign: 'center' }}>
                  <img src={`data:image/jpeg;base64,${item.ecardImage}`} alt="eCard" style={{ width: '100%', objectFit: 'contain', borderRadius: '8px', marginBottom: '10px' }} />
                </div>
              ) : item.valueName === null && item.ecardImage === null && item.externalDocuments != null ? (
                <div style={{ marginTop: '15px', textAlign: 'center' }}>
                  <img src={`data:image/png;base64,${item.externalDocuments}`} alt="External Document" style={{ width: '100%', objectFit: 'contain', borderRadius: '8px', marginBottom: '10px' }} />
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: '14px', margin: '5px 0 10px 0', wordWrap: 'break-word', whiteSpace: 'normal', textAlign: 'justify' }}>
              {extractCitation(item.description)}
            </p>
          </div>
        )}

        {item.ecardImage && item.nomiationId == null && (
          <div style={{ marginTop: '15px', textAlign: 'center' }}>
            <img src={`data:image/jpeg;base64,${item.ecardImage}`} alt="eCard" style={{ width: '100%', objectFit: 'contain', borderRadius: '8px', marginBottom: '10px' }} />
          </div>
        )}

        <div style={{ display: 'flex', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f0f0f0', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Popover
              content={
                <div style={{ display: 'flex', gap: '10px' }}>
                  {emojiOptions.map(({ code, emoji }) => (
                    <Tooltip key={code} title={code}>
                      <span style={{ fontSize: '20px', cursor: 'pointer' }} onClick={() => onEmojiReaction(item.feedId, code)}>
                        {emoji}
                      </span>
                    </Tooltip>
                  ))}
                </div>
              }
              trigger="hover"
            >
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginRight: '4px', padding: '5px 10px', borderRadius: '8px', transition: 'background-color 0.3s ease, color 0.3s ease' }} className="hover-box">
                <span style={{ fontSize: '18px', marginRight: '5px' }}>
                  {emojiReactions[item.feedId] ? emojiOptions.find(({ code }) => code === emojiReactions[item.feedId])?.emoji : <LikeOutlined />}
                </span>
                <span>{emojiReactions[item.feedId] ? emojiOptions.find(({ code }) => code === emojiReactions[item.feedId])?.code || "Reaction" : "Like"}</span>
              </div>
            </Popover>

            <div onClick={() => onToggleComments(item.feedId)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginRight: '20px', padding: '5px 10px', borderRadius: '8px', transition: 'background-color 0.3s ease, color 0.3s ease' }} className="hover-box">
              <CommentOutlined style={{ fontSize: '16px', marginRight: '5px' }} />
              <span>Comment</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
            <span>
              {(item.likeCount ?? []).length > 0 ? (
                <>
                  {(() => {
                    const firstUser = item.likeCount?.[0] ?? null;
                    const firstName = firstUser ? firstUser.item2.split(' ')[0] : '';
                    const userEmoji = firstUser ? emojiOptions.find(({ code }) => code === firstUser.item3)?.emoji || '' : '';
                    return (
                      <>
                        {userEmoji} {firstName}
                      </>
                    );
                  })()}
                  {(item.likeCount ?? []).length > 1 && (
                    <>
                      <span> and </span>
                      <Popover
                        content={
                          <div>
                            {(item.likeCount ?? []).slice(1).map((like, index) => {
                              const firstName = like.item2.split(' ')[0];
                              const userEmoji = emojiOptions.find(({ code }) => code === like.item3)?.emoji || '';
                              return (
                                <div key={index}>
                                  {userEmoji} {firstName}
                                </div>
                              );
                            })}
                          </div>
                        }
                        trigger="hover"
                        placement="top"
                      >
                        <span style={{ cursor: 'pointer', padding: '4px 1px', borderRadius: '8px', transition: 'background-color 0.3s ease, color 0.3s ease' }} className="underline-on-hover">
                          {item.likeCount?.length} others
                        </span>
                      </Popover>
                    </>
                  )}
                </>
              ) : (
                ''
              )}
            </span>
          </div>
        </div>

        {visibleComments[item.feedId] !== false && (
          <div style={{ marginTop: '15px', padding: '10px', borderRadius: '5px' }}>
            <div style={{ display: 'flex', marginTop: '10px', marginBottom: '10px', alignItems: 'center' }}>
              <Avatar
                src={userProfileImages[nominatorEmail] ? `data:image/jpeg;base64,${userProfileImages[nominatorEmail]}` : undefined}
                icon={<UserOutlined />}
                size={32}
                style={{ marginRight: '5px' }}
              />
              <div style={{ flexGrow: 1, position: 'relative' }}>
                <Input
                  placeholder="Write a comment... (type @ to tag someone)"
                  value={commentValues[item.feedId] || ''}
                  onChange={(e) => onCommentChange(item.feedId, e.target.value)}
                  style={{ borderRadius: '20px', backgroundColor: '#f0f0f0', border: 'none' }}
                  onPressEnter={() => !postingComment[item.feedId] && onComment(item.feedId)}
                  onKeyDown={(e) => { if (e.key === 'Escape') onClearMentions(item.feedId); }}
                  disabled={postingComment[item.feedId]}
                  prefix={
                    <Popover
                      content={emojiPickerContent}
                      trigger="click"
                      open={smileyOpen}
                      onOpenChange={setSmileyOpen}
                      placement="topLeft"
                    >
                      <SmileOutlined style={{ color: '#8629A4', cursor: 'pointer', fontSize: '16px' }} />
                    </Popover>
                  }
                  suffix={
                    <Button
                      type="text"
                      icon={<SendOutlined />}
                      onClick={() => onComment(item.feedId)}
                      loading={postingComment[item.feedId]}
                      disabled={postingComment[item.feedId] || !(commentValues[item.feedId]?.trim())}
                      style={{ backgroundColor: 'none', border: 'none', color: '#1890ff', cursor: 'pointer', boxShadow: 'none', borderRadius: '54%', padding: '0px 0px 0px 9px' }}
                    />
                  }
                />
                {(mentionResults[item.feedId] || []).length > 0 && (
                  <div style={{
                    position: 'absolute', bottom: '110%', left: 0, right: 0, zIndex: 9999,
                    background: '#fff', border: '1px solid #e8e8e8', borderRadius: '8px',
                    boxShadow: '0 -4px 12px rgba(0,0,0,0.12)', marginBottom: '4px', overflow: 'hidden'
                  }}>
                    {(mentionResults[item.feedId] || []).map(user => (
                      <div
                        key={user.id}
                        onMouseDown={(e) => { e.preventDefault(); onMentionSelect(item.feedId, user.id, user.displayName); }}
                        style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', color: '#333' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f5f0ff')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <UserOutlined style={{ marginRight: '8px', color: '#8629A4' }} />
                        {user.displayName}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {comments[item.feedId] && comments[item.feedId].slice(0, showAllComments[item.feedId] ? comments[item.feedId].length : 2).map((comment, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px', padding: '10px', borderRadius: '8px', backgroundColor: '#f9f9f9', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                <Avatar
                  src={userProfileImages[nominatorEmail] ? `data:image/jpeg;base64,${userProfileImages[nominatorEmail]}` : undefined}
                  icon={<UserOutlined />}
                  size={40}
                  style={{ marginRight: '10px' }}
                />
                <div style={{ flex: 1 }}>
                  <strong style={{ display: 'block', fontSize: '14px', color: '#333', marginBottom: '5px' }}>
                    {userNames[comment.item1] || 'Unknown User'}
                  </strong>
                  <p style={{ fontSize: '14px', color: '#555', margin: 0, wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {comment.item2.split(/(@\[[^\]]+\])/g).map((part, i) =>
                      /^@\[.+\]$/.test(part) ? (
                        <span key={i} style={{ color: '#8629A4', fontWeight: 600 }}>@{part.slice(2, -1)}</span>
                      ) : part
                    )}
                  </p>
                </div>
              </div>
            ))}
            {comments[item.feedId] && comments[item.feedId].length > 2 && (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <Button type="link" onClick={() => onToggleShowAllComments(item.feedId)}>
                  {showAllComments[item.feedId] ? 'Show less' : 'Show all'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </List.Item>
  );
});

FeedItem.displayName = 'FeedItem';

const Feeds: React.FC = () => {
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [filteredFeeds, setFilteredFeeds] = useState<Feed[]>([]);
  const [nominatorEmail, setNominatorEmail] = useState('');
  const [nominatorName, setNominatorName] = useState('');
  const [userProfileImages, setUserProfileImages] = useState<{ [key: string]: string | null }>({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProfileImage, setSelectedProfileImage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [activeTab, setActiveTab] = useState('celebrate');
  const [commentValue, setCommentValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentValues, setCommentValues] = useState<{ [key: string]: string }>({});
  const { instance, accounts } = useMsal();
  const [likeCounts, setLikeCounts] = useState<{ [key: string]: number }>({});
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [comments, setComments] = useState<{ [key: string]: { item1: string, item2: string }[] }>({});
  const [userNames, setUserNames] = useState<{ [key: string]: string }>({});
  // Add state to manage visibility of comments - default to true to show comments by default
  const [visibleComments, setVisibleComments] = useState<{ [key: string]: boolean }>({});
  const [showAllComments, setShowAllComments] = useState<{ [key: string]: boolean }>({});
  const [feedVisibility, setFeedVisibility] = useState<'public' | 'private'>('public'); // Add state for visibility
  const [emojiReactions, setEmojiReactions] = useState<{ [key: string]: string | null }>({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [postingComment, setPostingComment] = useState<{ [key: string]: boolean }>({}); // Track posting state for each feed
  const [mentionResults, setMentionResults] = useState<{ [feedId: string]: { id: string; displayName: string }[] }>({});
  const [pendingMentions, setPendingMentions] = useState<{ [feedId: string]: { id: string; displayName: string }[] }>({});
  const mentionSearchTimers = useRef<{ [feedId: string]: ReturnType<typeof setTimeout> }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useTranslation();

  // Performance optimization: Token caching
  const [cachedToken, setCachedToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<number>(0);
  const profileDataFetched = useRef(false);
  const userNameFetchQueue = useRef<Set<string>>(new Set());
  const userNameFetchTimer = useRef<NodeJS.Timeout | null>(null);

  // Prevent concurrent fetchData calls (replaces stale `loading` state guard)
  const fetchingRef = useRef(false);
  const searchTermRef = useRef(''); // ref so fetchData can read latest value without it being a dep

  // Per-tab data cache — shows previously fetched data instantly on revisit
  const tabCacheRef = useRef<Map<string, { feeds: Feed[]; total: number; ts: number }>>(new Map());
  const TAB_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

  // URL parameters for specific feeds
  const [searchParams] = useSearchParams();
  const appreciationId = searchParams.get('appreciationId');
  const celebrationId = searchParams.get('celebrationId');
  const nominationId = searchParams.get('nominationId');
  const [isSpecificFeedMode, setIsSpecificFeedMode] = useState(false);

  // Add useEffect to handle URL parameter changes and fetch data accordingly
  useEffect(() => {
    if (!isAuthReady) return; // Wait for authentication to be ready

    if (appreciationId || celebrationId || nominationId) {
      setIsSpecificFeedMode(true);
      setCurrentPage(1); // Reset to first page for specific feeds
      setFeeds([]); // Clear existing feeds
      setFilteredFeeds([]); // Clear filtered feeds
      fetchData();
    } else {
      setIsSpecificFeedMode(false);
      // You may want to fetch general data here or let existing logic handle it
    }
  }, [appreciationId, celebrationId, nominationId, isAuthReady]);

  const emojiOptions = [
    { code: 'Like', emoji: '👍' },
    { code: 'Heart', emoji: '❤️' },
    { code: 'Big smile', emoji: '😁' },
    { code: 'Surprised', emoji: '😮' },
    { code: 'Celebrate', emoji: '🥳' },
    { code: 'Appluase', emoji: '👏' },
  ];
  const imageMapping: { [key: string]: { [key: string]: string } } = {
    accelerateinnovation: {
      Cheers: accelerateinnovationCheers,
      Stellar: accelerateinnovationStellar,
      "Extra Mile": accelerateinnovationExtraMile,
      // GEM: accelerateinnovationGem,
    },
    beagile: {
      Cheers: beagileCheers,
      Stellar: beagileStellar,
      "Extra Mile": beagileExtraMile,
      // GEM: beagileGem,
    },
    growtogether: {
      Cheers: growTogetherCheers,
      Stellar: growTogetherStellar,
      "Extra Mile": growTogetherExtraMile,
      // GEM: growTogetherGem,
    },
    driveresults: {
      Cheers: driveResultsCheers,
      Stellar: driveResultsStellar,
      "Extra Mile": driveResultsExtraMile,
      // GEM: driveResultsGem,
    },
  };

  const handleEmojiReaction = async (feedId: string, emojiCode: string) => {
    const userId = localStorage.getItem("id");
    const feedInteractionData = {
      userId: userId,
      feedId: feedId,
      interactionType: 'EMOJI_REACTION',
      emojiCode: emojiCode,
    };

    try {
      const token = await fetchToken();
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Feeds/Likes`, feedInteractionData, {
        headers: { Authorization: `Bearer ${token}` },
      });


      // Update the emoji reaction state based on the response
      if (response.data === 1) {
        // If the response is 1, set the emoji reaction
        setEmojiReactions((prevReactions) => ({
          ...prevReactions,
          [feedId]: emojiCode,
        }));
      } else if (response.data === 0) {
        // If the response is 0, remove the emoji reaction
        setEmojiReactions((prevReactions) => ({
          ...prevReactions,
          [feedId]: null,
        }));
      }
    } catch (error) {
      console.error('Error toggling emoji reaction:', error);
    }
  };

  // Optimized: Memoize pure function to prevent recalculation
  const getImageSrc = useCallback((valueName: string | undefined, rewardName: string | undefined): string | null => {
    if (!valueName || !rewardName) return null;
    const normalizedValueName = valueName.toLowerCase().replace(/\s+/g, '');
    return imageMapping[normalizedValueName]?.[rewardName] || null;
  }, []);

  const toggleComments = (feedId: string) => {
    setVisibleComments(prevState => ({
      ...prevState,
      [feedId]: !prevState[feedId]
    }));
    setShowAllComments(prevState => ({
      ...prevState,
      [feedId]: !prevState[feedId] ? false : prevState[feedId]
    }));
  };

  const handleVisibilityChange = (visibility: 'public' | 'private') => {
    setFeedVisibility(visibility); // Update the state
    setFeeds([]); // Clear the feeds
    setFilteredFeeds([]); // Clear the filtered feeds
    setCurrentPage(1); // Reset the current page to 1
  };

  // feedVisibility changes are handled by the tab/visibility effect below

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const toggleShowAllComments = (feedId: string) => {
    setShowAllComments(prevState => ({
      ...prevState,
      [feedId]: !prevState[feedId]
    }));
  };

  // Function to generate avatar based on name
  const generateAvatar = (name: string) => {
    return (
      <BoringAvatar
        size={40}
        name={name}
        variant="beam"
        colors={["#92A1C6", "#146A7C", "#F0AB3D", "#C271B4", "#C20D90"]}
      />
    );
  };
  const callMsGraph = async (token: any) => {
    const headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);
    const response = await fetch(`https://graph.microsoft.com/v1.0/me?`, {
      method: "GET",
      headers,
    }).then((response) => response.json());
    return response;
  };

  // Optimized: Batch user name fetching
  const fetchUserName = useCallback((userId: string) => {
    if (userNames[userId]) return; // If the user name is already fetched, do not fetch again
    
    // Add to queue
    userNameFetchQueue.current.add(userId);
    
    // Clear existing timer
    if (userNameFetchTimer.current) {
      clearTimeout(userNameFetchTimer.current);
    }
    
    // Set new timer to batch fetch after 100ms
    userNameFetchTimer.current = setTimeout(async () => {
      const userIdsToFetch = Array.from(userNameFetchQueue.current);
      userNameFetchQueue.current.clear();
      
      if (userIdsToFetch.length === 0) return;
      
      try {
        const token = await fetchToken();
        
        // Fetch all users in parallel
        const responses = await Promise.all(
          userIdsToFetch.map(id => 
            axios.get(`${process.env.REACT_APP_API_URL}/api/Users/getUser/${id}`, 
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(error => {
              console.error(`Error fetching user ${id}:`, error);
              return null;
            })
          )
        );
        
        const newUserNames: { [key: string]: string } = {};
        responses.forEach((response, index) => {
          if (response && response.status === 200) {
            newUserNames[userIdsToFetch[index]] = response.data.displayName;
          }
        });
        
        setUserNames(prevUserNames => ({
          ...prevUserNames,
          ...newUserNames
        }));
      } catch (error) {
        console.error('Error fetching user names:', error);
      }
    }, 100);
  }, [userNames]);

  // Optimized: Token caching with 5-minute expiration
  const fetchToken = useCallback(async () => {
    const now = Date.now();
    
    // Return cached token if still valid
    if (cachedToken && tokenExpiry > now) {
      return cachedToken;
    }
    
    // Fetch new token
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    
    const token = res.idToken;
    const expiry = now + (5 * 60 * 1000); // 5 minutes from now
    
    setCachedToken(token);
    setTokenExpiry(expiry);
    
    return token;
  }, [instance, accounts, cachedToken, tokenExpiry]);

  useEffect(() => {
    fetchToken();
  }, []);

  // Optimized: Prevent duplicate profile data requests
  const requestProfileData = useCallback(async () => {
    // Prevent duplicate calls
    if (profileDataFetched.current) return;
    profileDataFetched.current = true;
    
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["User.Read", "User.Read.All"],
        authority: `https://login.microsoftonline.com/560ec2b0-df0c-4e8c-9848-a15718863bb6`,
      });
      const data = await callMsGraph(response.accessToken);
      localStorage.setItem("UserMail", data?.mail.toLowerCase());
      localStorage.setItem("id", data?.id);
      setNominatorName(data?.displayName || 'Default Nominator');
      setNominatorEmail(data?.mail.toLowerCase());
      if (data?.id && data?.displayName) {
        setUserNames(prev => ({ ...prev, [data.id]: data.displayName }));
      }
      setIsAuthReady(true);
    } catch (error) {
      console.error("Silent token acquisition failed, acquiring token using redirect");
      instance.loginRedirect(loginRequest);
      setIsAuthReady(false);
      profileDataFetched.current = false; // Reset on error
    }
  }, [instance]);

  useEffect(() => {
    requestProfileData();
  }, [requestProfileData]);

  // Keep searchTermRef in sync so fetchData can read the latest value without it being a dep
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  // Check if user is already authenticated on component mount
  useEffect(() => {
    if (accounts && accounts.length > 0 && localStorage.getItem("UserMail")) {
      setIsAuthReady(true);
    }
  }, [accounts]);



  // Optimized: Smart caching - only fetch uncached user profile images
  const fetchUserProfileImages = async (userIds: string[]): Promise<void> => {
    try {
      // Filter out already cached user IDs
      const uncachedUserIds = userIds.filter(id => !userProfileImages[id]);
      
      // If all users are cached, skip the API call
      if (uncachedUserIds.length === 0) {
        return;
      }
      
      const token = await fetchToken();
      const params = new URLSearchParams();
      uncachedUserIds.forEach(id => params.append('userIds', id));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Users/GetProfileImages?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error fetching user profile images: ${response.statusText}`);
      }

      const data: { [key: string]: string | null } = await response.json();
      
      // Merge new images with existing cache
      setUserProfileImages(prevImages => ({
        ...prevImages,
        ...data
      }));
    } catch (error) {
      console.error('Error fetching user profile images:', error);
    }
  };

  // Optimized: Memoize pure function to prevent recalculation
  const extractCitation = useCallback((description: string): string => {
    const citationRegex = /~citation~([\s\S]*?)~FormFields~|~citation~([\s\S]*)/;
    const match = description.match(citationRegex);
    return match ? (match[1] || match[2] || '').trim() : description;
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async (page: number = 1, replace: boolean = true, customPageSize?: number) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      const token = await fetchToken();
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userMail = localStorage.getItem("UserMail") || '';
      const userId = localStorage.getItem("id");
      let data: any[] = [];
      let totalCount = 0;

      const ps = customPageSize ?? itemsPerPage;
      const searchParam = searchTermRef.current.trim() ? `&search=${encodeURIComponent(searchTermRef.current.trim())}` : '';

      if (appreciationId) {
        setIsSpecificFeedMode(true);
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Feeds/Appreciations?email=${encodeURIComponent(userMail)}&page=${page}&pageSize=${ps}&userTimeZone=${encodeURIComponent(userTimeZone)}&visibility=Company&appreciationId=${appreciationId}`, { headers: { Authorization: `Bearer ${token}` } });
        data = res.data.items || [];
        totalCount = res.data.totalCount || 0;
        data.sort((a: any, b: any) => new Date(b.feedDate || b.createdAt).getTime() - new Date(a.feedDate || a.createdAt).getTime());
      } else if (celebrationId) {
        setIsSpecificFeedMode(true);
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Feeds/Celebrations?email=${encodeURIComponent(userMail)}&page=${page}&pageSize=${ps}&userTimeZone=${encodeURIComponent(userTimeZone)}&visibility=Company&celebrationId=${celebrationId}`, { headers: { Authorization: `Bearer ${token}` } });
        data = res.data.items || [];
        totalCount = res.data.totalCount || 0;
        data.sort((a: any, b: any) => new Date(b.feedDate || b.createdAt).getTime() - new Date(a.feedDate || a.createdAt).getTime());
      } else if (nominationId) {
        setIsSpecificFeedMode(true);
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Feeds/Rewards?email=${encodeURIComponent(userMail)}&page=${page}&pageSize=${ps}&userTimeZone=${encodeURIComponent(userTimeZone)}&visibility=Company&nominationId=${nominationId}`, { headers: { Authorization: `Bearer ${token}` } });
        data = res.data.items || [];
        totalCount = res.data.totalCount || 0;
        data.sort((a: any, b: any) => new Date(b.transactionDate || b.feedDate || b.createdAt).getTime() - new Date(a.transactionDate || a.feedDate || a.createdAt).getTime());
      } else if (activeTab === 'all') {
        setIsSpecificFeedMode(false);
        const [appRes, celRes, rewRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/Feeds/Appreciations?email=${encodeURIComponent(userMail)}&page=${page}&pageSize=${ps}&userTimeZone=${encodeURIComponent(userTimeZone)}&visibility=${feedVisibility}${searchParam}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/Feeds/Celebrations?email=${encodeURIComponent(userMail)}&page=${page}&pageSize=${ps}&userTimeZone=${encodeURIComponent(userTimeZone)}&visibility=${feedVisibility}${searchParam}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/Feeds/Rewards?email=${encodeURIComponent(userMail)}&visibility=${feedVisibility}&page=${page}&pageSize=${ps}${searchParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        data = [...(appRes.data.items || []), ...(celRes.data.items || []), ...(rewRes.data.items || [])];
        totalCount = (appRes.data.totalCount || 0) + (celRes.data.totalCount || 0) + (rewRes.data.totalCount || 0);
        data.sort((a: any, b: any) => {
          const dA = a.nomiationId ? (a.transactionDate || a.feedDate || a.createdAt) : (a.feedDate || a.createdAt);
          const dB = b.nomiationId ? (b.transactionDate || b.feedDate || b.createdAt) : (b.feedDate || b.createdAt);
          return new Date(dB).getTime() - new Date(dA).getTime();
        });
      } else {
        setIsSpecificFeedMode(false);
        const endpointMap: Record<string, string> = {
          appreciate: `${process.env.REACT_APP_API_URL}/api/Feeds/Appreciations?email=${encodeURIComponent(userMail)}&page=${page}&pageSize=${ps}&userTimeZone=${encodeURIComponent(userTimeZone)}&visibility=${feedVisibility}${searchParam}`,
          celebrate:  `${process.env.REACT_APP_API_URL}/api/Feeds/Celebrations?email=${encodeURIComponent(userMail)}&page=${page}&pageSize=${ps}&userTimeZone=${encodeURIComponent(userTimeZone)}&visibility=${feedVisibility}${searchParam}`,
          rewards:    `${process.env.REACT_APP_API_URL}/api/Feeds/Rewards?email=${encodeURIComponent(userMail)}&visibility=${feedVisibility}&page=${page}&pageSize=${ps}${searchParam}`,
        };
        const endpoint = endpointMap[activeTab] || `${process.env.REACT_APP_API_URL}/api/Feeds/All?email=${encodeURIComponent(userMail)}&page=${page}&pageSize=${ps}&visibility=${feedVisibility}${searchParam}`;
        const res = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });
        data = res.data.items || [];
        totalCount = res.data.totalCount || 0;
        data.sort((a: any, b: any) =>
          activeTab === 'rewards'
            ? new Date(b.transactionDate || b.feedDate || b.createdAt).getTime() - new Date(a.transactionDate || a.feedDate || a.createdAt).getTime()
            : new Date(b.feedDate || b.createdAt).getTime() - new Date(a.feedDate || a.createdAt).getTime()
        );
      }

      const processed: Feed[] = data.map((item: any) => ({
        ...item,
        comments: item.comments ? item.comments.map((c: any) => ({ item1: c.item1, item2: c.item2 })) : [],
      }));

      const sortedDesc = (arr: Feed[]) =>
        [...arr].sort((a: any, b: any) => {
          const dA = a.nomiationId ? (a.transactionDate || a.feedDate || a.createdAt) : (a.feedDate || a.createdAt);
          const dB = b.nomiationId ? (b.transactionDate || b.feedDate || b.createdAt) : (b.feedDate || b.createdAt);
          return new Date(dB).getTime() - new Date(dA).getTime();
        });

      if (replace) {
        // Tab change or first load — replace feeds entirely
        setFeeds(sortedDesc(processed));
        setFilteredFeeds(sortedDesc(processed));
        setHasMoreData(true);
        // Save to per-tab cache
        const cacheKey = `${activeTab}_${feedVisibility}`;
        tabCacheRef.current.set(cacheKey, { feeds: sortedDesc(processed), total: totalCount, ts: Date.now() });
      } else {
        // Pagination append — merge using functional setState (avoids stale closure on `feeds`)
        setFeeds(prev => {
          const merged = Array.from(new Map([...prev, ...processed].map(f => [f.feedId, f])).values());
          return sortedDesc(merged);
        });
        setFilteredFeeds(prev => {
          const merged = Array.from(new Map([...prev, ...processed].map(f => [f.feedId, f])).values());
          return sortedDesc(merged);
        });
        // If the API returned no items, there's nothing more to load
        if (processed.length === 0) {
          setHasMoreData(false);
        }
      }

      setTotalItems(totalCount);

      // Emoji reactions — merge with existing
      const updatedReactions: { [key: string]: string } = {};
      processed.forEach(feed => {
        const userReaction = (feed.likeCount as any[])?.find((like: any) => like.item1 === userId);
        if (userReaction) updatedReactions[feed.feedId] = userReaction.item3;
      });
      setEmojiReactions(prev => ({ ...prev, ...updatedReactions }));

      // Batch fetch profile images for new users only
      const userIds = Array.from(new Set(data.flatMap((item: any) => [item.nominatorId, item.nomineeId])));
      fetchUserProfileImages(userIds);

      // Fetch user names for comments
      processed.forEach(item => item.comments?.forEach((c: any) => fetchUserName(c.item1)));

      // Merge comments
      setComments(prev => {
        const updated = { ...prev };
        processed.forEach(item => { updated[item.feedId] = item.comments || []; });
        return updated;
      });

    } catch (error) {
      console.error('Error in fetchData:', error);
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [fetchToken, activeTab, feedVisibility, appreciationId, celebrationId, nominationId, itemsPerPage]);

  // Effect 1: Tab or visibility change — show cached data instantly, then fetch fresh
  useEffect(() => {
    if (!isAuthReady || appreciationId || celebrationId || nominationId) return;

    const cacheKey = `${activeTab}_${feedVisibility}`;
    const cached = tabCacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.ts < TAB_CACHE_TTL) {
      // Serve cached data immediately so tab feels instant
      setFeeds(cached.feeds);
      setFilteredFeeds(cached.feeds);
      setTotalItems(cached.total);
    } else {
      setFeeds([]);
      setFilteredFeeds([]);
    }
    setCurrentPage(1);
    setSearchTerm('');
    setSearchInputValue('');
    setHasMoreData(true);
    fetchData(1, true); // always replace on tab/visibility switch
  }, [activeTab, feedVisibility, isAuthReady, fetchData]);

  // Effect 2: Pagination only — fires when user goes to page 2, 3, etc.
  useEffect(() => {
    if (!isAuthReady || currentPage === 1) return; // page 1 is handled by Effect 1
    if (appreciationId || celebrationId || nominationId) return;
    fetchData(currentPage, false); // append for infinite scroll
  }, [currentPage, isAuthReady, fetchData]);

  // Effect 3: URL-param specific feeds (deep link to a single item)
  useEffect(() => {
    if (!isAuthReady) return;
    if (!appreciationId && !celebrationId && !nominationId) return;
    setFeeds([]);
    setFilteredFeeds([]);
    setCurrentPage(1);
    fetchData(1, true);
  }, [appreciationId, celebrationId, nominationId, isAuthReady, fetchData]);

  // Effect 4: Server-side search — refetch from server when search term changes
  useEffect(() => {
    if (!isAuthReady || appreciationId || celebrationId || nominationId) return;
    setCurrentPage(1);
    setHasMoreData(true);
    tabCacheRef.current.clear();
    fetchData(1, true);
  }, [searchTerm, isAuthReady]);

  // Effect 5: Auto-refresh every 60 seconds (only active tab, no URL params)
  useEffect(() => {
    if (!isAuthReady || appreciationId || celebrationId || nominationId) return;
    const interval = setInterval(() => fetchData(1, true), 60000);
    return () => clearInterval(interval);
  }, [activeTab, feedVisibility, isAuthReady, fetchData]);

  const formatDate = (date: string) => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const feedDate = moment(date).tz(userTimeZone); // Convert API date to user's timezone
    
    // Always show the date in consistent format
    return feedDate.format('MMM Do, YYYY'); // Example: "Feb 21st, 2026"
  };
  // Get current time in user’s timezone
  //     const updatedAt = moment(date).tz(userTimeZone); // Convert API date to user’s timezone
  //     const diffInHours = now.diff(updatedAt, 'hours');

  //     if (diffInHours < 24) {
  //         return updatedAt.fromNow(); // Show "X hours ago" if within 24 hours
  //     } else {
  //         return updatedAt.format('MMMM Do YYYY, h:mm A'); // Example: "March 5th 2025, 2:30 PM"
  //     }
  // };
  // Function to get the names of users who reacted
  const getReactionTooltipContent = (likeCount: { item1: string; item2: string }[]) => {
    if (!likeCount || likeCount.length === 0) return null;

    // Map item1 (user IDs) directly
    const userIdsList = likeCount.map((like) => like.item1);
    const firstUserId = userIdsList[0];
    const othersCount = userIdsList.length - 1;

    return (
      <>
        <span>{firstUserId}</span>
        {othersCount > 0 && <span> and {othersCount} others</span>}
        <div style={{ marginTop: '5px' }}>
          {userIdsList.map((id, index) => (
            <div key={index}>{id}</div>
          ))}
        </div>
      </>
    );
  };

  const handleNameClick = (userId: string) => {
    setSelectedProfileImage(userProfileImages[userId]);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedProfileImage(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleTabChange = (key: string) => {
    // State reset + data fetch is handled entirely by Effect 1 when activeTab changes
    setActiveTab(key);
  };

  const handleMenuClick = (e: any) => {
    const { key } = e;
    if (key === 'public' || key === 'private') {
      handleVisibilityChange(key); // Update visibility
    }
  };

  const handleLike = async (feedId: string) => {
    const userId = localStorage.getItem("id");
    const feedinteractiondata = {
      userId: userId,
      feedId: feedId,
      interactionType: 'LIKE',
    };

    try {
      const token = await fetchToken();
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Feeds/Likes`, feedinteractiondata, { headers: { Authorization: `Bearer ${token}` } });

      const newLikeCount = response.data;

      setFeeds(prevFeeds =>
        prevFeeds.map(feed =>
          feed.feedId === feedId
            ? {
              ...feed,
              liked: !feed.liked,
            }
            : feed
        )
      );

      setLikeCounts(prevLikeCounts => ({
        ...prevLikeCounts,
        [feedId]: newLikeCount
      }));
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  // Optimized: Debounced comment posting to prevent rapid submissions
  const handleComment = useCallback(async (feedId: string) => {
    const comment = commentValues[feedId];
    if (!comment.trim()) return;

    // Prevent duplicate submissions
    if (postingComment[feedId]) return;

    const userId = localStorage.getItem("id") || '';
    // Encode pending mentions as @[Name] in stored text so display can highlight them
    let storedComment = comment;
    for (const mention of (pendingMentions[feedId] || [])) {
      storedComment = storedComment.replace(`@${mention.displayName}`, `@[${mention.displayName}]`);
    }
    const feedinteractiondata = {
      userId: userId,
      feedId: feedId,
      interactionType: 'COMMENT',
      commentText: storedComment,
      mentionedUserIds: (pendingMentions[feedId] || []).map(m => m.id)
    };

    setPostingComment((prev) => ({ ...prev, [feedId]: true }));

    try {
      const token = await fetchToken();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/Feeds/Comments`, feedinteractiondata, { headers: { Authorization: `Bearer ${token}` } });

      setComments(prevComments => ({
        ...prevComments,
        [feedId]: [{ item1: userId, item2: storedComment }, ...(prevComments[feedId] || [])]
      }));

      // Fetch the user name for the new comment
      fetchUserName(userId);

      // Clear the comment input and pending mentions for the specific feed item
      setCommentValues((prevValues) => ({ ...prevValues, [feedId]: '' }));
      setPendingMentions(prev => ({ ...prev, [feedId]: [] }));
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setPostingComment((prev) => ({ ...prev, [feedId]: false })); // Re-enable the button
    }
  }, [commentValues, postingComment, fetchToken, fetchUserName]);

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="public">Public</Menu.Item>
      <Menu.Item key="private">Private</Menu.Item>
    </Menu>
  );
  const handleCommentChange = (feedId: string, value: string) => {
    setCommentValues(prevValues => ({ ...prevValues, [feedId]: value }));

    // Detect trailing @query (letters, spaces after @)
    const match = value.match(/@([\w][^@]*)$/);
    if (match) {
      const query = match[1].trim();
      if (query.length > 0) {
        if (mentionSearchTimers.current[feedId]) clearTimeout(mentionSearchTimers.current[feedId]);
        mentionSearchTimers.current[feedId] = setTimeout(async () => {
          try {
            const token = await fetchToken();
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/Search`, {
              params: { search: query },
              headers: { Authorization: `Bearer ${token}` }
            });
            setMentionResults(prev => ({ ...prev, [feedId]: (res.data || []).slice(0, 6) }));
          } catch { /* ignore */ }
        }, 300);
      }
    } else {
      setMentionResults(prev => ({ ...prev, [feedId]: [] }));
    }
  };

  const handleMentionSelect = useCallback((feedId: string, userId: string, displayName: string) => {
    setCommentValues(prev => {
      const current = prev[feedId] || '';
      // Keep clean text — no brackets visible to the user
      const updated = current.replace(/@[\w][^@]*$/, `@${displayName} `);
      return { ...prev, [feedId]: updated };
    });
    setPendingMentions(prev => ({
      ...prev,
      [feedId]: [...(prev[feedId] || []).filter(m => m.id !== userId), { id: userId, displayName }]
    }));
    setMentionResults(prev => ({ ...prev, [feedId]: [] }));
  }, []);

  // Show loading state if authentication is not ready and we have URL parameters
  if (!isAuthReady && (appreciationId || celebrationId || nominationId)) {
    return (
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <h3>Loading your specific feed...</h3>
          <p>Please wait while we authenticate your session.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      // width: '62rem'
    }}
      className="feeds-container"
    >

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
        backgroundColor: 'white',
        borderRadius: '8px',

      }}>
        <Breadcrumb
          style={{
            backgroundColor: 'white',
            padding: '10px 20px',
            // borderRadius: '8px',
            // boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <Breadcrumb.Item>
            <Link to="/home">
              <HomeOutlined style={{ marginRight: '5px' }} />
              {t('home')}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{t('my_feeds')}</Breadcrumb.Item>
        </Breadcrumb>
        <h4 style={{
          backgroundColor: 'white',
          padding: '10px 20px',
          // borderRadius: '8px',
          // boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          margin: 0
        }}>
          {t('what_happening')}, {nominatorName.split(' ')[0]}!
        </h4>
      </div>

      {/* <div style={{ 
        padding: '20px', 
        marginBottom: '20px', 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ marginBottom: '10px' }}>What's happening, {nominatorName.split(' ')[0]}!</h1>
        <p style={{ color: '#666' }}>
          Catch up on the latest buzz! Scroll through the feeds to see posts like Appreciation, Celebration and Rewards.
          Dive into conversations, share your thoughts, and discover new things. Ready to explore?
        </p>
      </div> */}

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', backgroundColor: 'white',
        padding: '15px 20px',
        borderRadius: '8px',
        flexDirection: isMobile ? 'column' : 'row', // Change flexDirection based on device size
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        gap: isMobile ? '15px' : '0',
      }}>
        {isSpecificFeedMode ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button
              onClick={() => window.location.href = '/awards/feeds'}
              type="default"
              style={{ marginRight: '10px' }}
            >
              ← Back to All Feeds
            </Button>
            <h3 style={{ margin: 0, color: '#1890ff' }}>
              {appreciationId ? 'Appreciation' : nominationId ? 'Nomination' : 'Celebration'}
            </h3>
          </div>
        ) : (
          <div style={{ 
            width: isMobile ? '100%' : 'auto',
            overflowX: isMobile ? 'auto' : 'visible',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            position: 'relative',
          }} className="tabs-wrapper">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              type="card"
              style={{
                width: isMobile ? 'max-content' : 'auto',
                minWidth: isMobile ? '100%' : 'auto',
              }}
              tabBarStyle={{
                marginBottom: 0,
                flexWrap: 'nowrap',
                border: 'none',
              }}
            >
              <TabPane tab={t('celebrate')} key="celebrate" />
              <TabPane tab={t('appreciate')} key="appreciate" />
              <TabPane tab={t('rewards')} key="rewards" />
              <TabPane
                tab={feedVisibility === 'public' ? t('all_feeds') : t('my_feeds')}
                key="all"
              />
            </Tabs>
          </div>
        )}

        {!isSpecificFeedMode && (
          <Dropdown overlay={menu} trigger={['click']} >
            <Button style={{ 
              marginLeft: isMobile ? '0' : '10px', 
              position: 'relative', 
              bottom: isMobile ? '0' : '5px',
              alignSelf: isMobile ? 'flex-start' : 'auto',
            }}>
              {feedVisibility === 'public' ? 'Public' : 'Private'} ▼
            </Button>
          </Dropdown>
        )}

      </div>

      {!isSpecificFeedMode && (
        <div style={{
          marginBottom: '10px',
          backgroundColor: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <Input
            placeholder="Search by nominee, nominator, reward or description..."
            value={searchInputValue}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInputValue(value);
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              if (!value) {
                setSearchTerm('');
              } else {
                searchDebounceRef.current = setTimeout(() => setSearchTerm(value), 300);
              }
            }}
            allowClear
            prefix={<SearchOutlined style={{ color: '#8629A4' }} />}
            style={{ borderRadius: '8px', fontSize: '14px' }}
            size="large"
          />
          {searchTerm.trim() && !loading && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#888' }}>
              {`${filteredFeeds.length} result${filteredFeeds.length !== 1 ? 's' : ''} found`}
            </div>
          )}
        </div>
      )}

      <InfiniteScroll
        dataLength={filteredFeeds.length}
        next={() => {
          setCurrentPage(prevPage => prevPage + 1);
        }}
        hasMore={feeds.length < totalItems && hasMoreData}
        loader={<h4>Loading...</h4>}
        endMessage={<p style={{ textAlign: 'center' }}>No more feeds to show</p>}
      >
        <List
          itemLayout="vertical"
          size="large"
          dataSource={filteredFeeds}
          loading={loading}
          style={{ padding: '0', borderRadius: '8px' }}
          renderItem={(item) => (
            <FeedItem
              item={item}
              userProfileImages={userProfileImages}
              emojiReactions={emojiReactions}
              visibleComments={visibleComments}
              showAllComments={showAllComments}
              commentValues={commentValues}
              postingComment={postingComment}
              comments={comments}
              userNames={userNames}
              nominatorEmail={nominatorEmail}
              onEmojiReaction={handleEmojiReaction}
              onToggleComments={toggleComments}
              onToggleShowAllComments={toggleShowAllComments}
              onCommentChange={handleCommentChange}
              onComment={handleComment}
              onNameClick={handleNameClick}
              formatDate={formatDate}
              getImageSrc={getImageSrc}
              extractCitation={extractCitation}
              emojiOptions={emojiOptions}
              mentionResults={mentionResults}
              onMentionSelect={handleMentionSelect}
              onClearMentions={(feedId) => setMentionResults(prev => ({ ...prev, [feedId]: [] }))}
            />
          )}
        />
      </InfiniteScroll>

      <Modal
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
        width={300}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          {selectedProfileImage ? (
            <img
              src={`data:image/jpeg;base64,${selectedProfileImage}`}
              alt="Profile"
              style={{ width: '200px', height: '200px', borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <Avatar size={200} icon={<UserOutlined />} />
          )}
        </div>
      </Modal>

      <style>
        {`
          .hover-box {
            background-color: transparent;
            color: inherit;
          }

          .hover-box:hover {
            background-color: #f0f8ff;
            color: #1890ff;
          }
            
          .underline-on-hover {
            text-decoration: none;
          }

          .underline-on-hover:hover {
            text-decoration: underline;
          }

          /* Mobile responsive tabs */
          @media (max-width: 768px) {
            .tabs-wrapper {
              -webkit-overflow-scrolling: touch;
              scrollbar-width: thin;
              display: block !important;
            }

            .tabs-wrapper::-webkit-scrollbar {
              height: 6px;
            }

            .tabs-wrapper::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 4px;
            }

            .tabs-wrapper::-webkit-scrollbar-thumb {
              background-color: rgba(0, 0, 0, 0.3);
              border-radius: 4px;
            }

            .tabs-wrapper::-webkit-scrollbar-thumb:hover {
              background-color: rgba(0, 0, 0, 0.5);
            }
            
            .tabs-wrapper .ant-tabs {
              overflow: visible !important;
            }

            .tabs-wrapper .ant-tabs-nav {
              margin-bottom: 0 !important;
              width: max-content !important;
              min-width: 100% !important;
            }
            
            .tabs-wrapper .ant-tabs-nav-wrap {
              overflow: visible !important;
              display: block !important;
            }

            .tabs-wrapper .ant-tabs-nav-operations {
              display: none !important;
            }
            
            .tabs-wrapper .ant-tabs-nav-list {
              display: flex !important;
              flex-wrap: nowrap !important;
              white-space: nowrap !important;
              width: max-content !important;
            }
            
            .tabs-wrapper .ant-tabs-tab {
              flex-shrink: 0 !important;
              padding: 8px 16px !important;
              font-size: 14px !important;
              margin: 0 4px 0 0 !important;
            }

            .tabs-wrapper .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
              border-radius: 4px 4px 0 0 !important;
            }

            .tabs-wrapper .ant-tabs-ink-bar {
              display: none !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Feeds;